/**
 * Peças de chrome compartilhadas entre as páginas do site (home-page.tsx e
 * partner-page.tsx) — extraído pra não duplicar/desalinhar entre elas.
 * Topbar/Hero ficam em cada página (nav e copy diferem o bastante pra não
 * valer abstrair).
 */
import { useEffect, useRef, useState } from "react";
import { useScrollAnimation, useCountUp } from "@/hooks/use-scroll-animation";
import { Parallax } from "@/components/engine/parallax";

// ============================================================================
// CUSTOM CURSOR
// ============================================================================
export function CustomCursor() {
  const cursorRef = useRef<HTMLDivElement>(null);
  const dotRef = useRef<HTMLDivElement>(null);
  const [hovering, setHovering] = useState(false);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (cursorRef.current) {
        cursorRef.current.style.transform = `translate(${e.clientX}px, ${e.clientY}px) translate(-50%, -50%)`;
      }
      if (dotRef.current) {
        dotRef.current.style.transform = `translate(${e.clientX}px, ${e.clientY}px) translate(-50%, -50%)`;
      }
    };
    const onOver = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      setHovering(!!t.closest("a, button, [data-cursor-hover]"));
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseover", onOver);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseover", onOver);
    };
  }, []);

  return (
    <>
      <div ref={cursorRef} className={`lv2-cursor ${hovering ? "is-hover" : ""}`} />
      <div ref={dotRef} className="lv2-cursor-dot" />
    </>
  );
}

// ============================================================================
// REVEAL — fade-up on scroll
// ============================================================================
export function Reveal({
  children,
  className = "",
  stagger = false,
  threshold = 0.15,
}: {
  children: React.ReactNode;
  className?: string;
  stagger?: boolean;
  threshold?: number;
}) {
  const { ref, isVisible } = useScrollAnimation<HTMLDivElement>({ threshold });
  return (
    <div
      ref={ref}
      className={`${stagger ? "lv2-reveal-stagger" : "lv2-reveal"} ${isVisible ? "is-visible" : ""} ${className}`}
    >
      {children}
    </div>
  );
}

// ============================================================================
// CHAPTER — wrapper genérico com número 001/002/etc
// ============================================================================
export function Chapter({
  id,
  number,
  eyebrow,
  title,
  children,
}: {
  id: string;
  number: string;
  eyebrow: string;
  title: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="lv2-chapter">
      <Parallax speed={0.45} className="lv2-chapter-bgnum">{number}</Parallax>
      <div className="lv2-chapter-num">CHAPTER {number}</div>
      <Reveal>
        <Parallax speed={-0.08}>
          <div className="lv2-chapter-eyebrow">{eyebrow}</div>
        </Parallax>
        <Parallax speed={-0.18}>
          <h2 className="lv2-display xl lv2-chapter-title">{title}</h2>
        </Parallax>
      </Reveal>
      <Reveal stagger threshold={0.1}>
        {children}
      </Reveal>
    </section>
  );
}

// ============================================================================
// STATS — genérico
// ============================================================================
export interface StatItem {
  /** Número anima (contador). String é renderizada como está — pra valores que não são
   *  quantidade ("17.09", "OMS · RMS · BACKOFFICE"). */
  value: number | string;
  label: string;
  suffix?: string;
  prefix?: string;
}

/**
 * `animate={false}` desliga o contador da linha inteira.
 *
 * Existe porque contador que sobe passa por valores INTERMEDIÁRIOS que o visitante lê como se
 * fossem o número real: a caminho de R$139 ele exibe R$137, e a caminho de 300 vagas exibe 297.
 * Num site de preço e escassez isso não é detalhe estético — alguém printa R$137 e cobra depois.
 * Onde o número é uma promessa comercial, ele nasce parado.
 */
export function StatsRow({ items, animate = true }: { items: StatItem[]; animate?: boolean }) {
  // `data-count` deixa o CSS montar a grade com o número REAL de itens. Antes era fixo em 4
  // colunas: uma linha de 3 stats deixava a quarta coluna vazia, e o vão à direita lia como
  // conteúdo faltando.
  return (
    <div className="lv2-stats" data-count={items.length}>
      {items.map((it) => (
        <Stat key={it.label} {...it} animate={animate} />
      ))}
    </div>
  );
}
function Stat({ value, label, suffix = "", prefix = "", animate = true }: StatItem & { animate?: boolean }) {
  const { ref, isVisible } = useScrollAnimation<HTMLDivElement>({ threshold: 0.4 });
  const numero = typeof value === "number";
  const { count, startAnimation } = useCountUp(numero ? (value as number) : 0, 1400);
  useEffect(() => {
    if (isVisible && numero && animate) startAnimation();
  }, [isVisible, numero, animate, startAnimation]);
  const exibido = !numero
    ? (value as string)
    : animate
      ? count.toLocaleString("pt-BR")
      : (value as number).toLocaleString("pt-BR");
  return (
    <div ref={ref} className="lv2-stat">
      {/* Valor textual ("OMS · RMS · Backoffice") no corpo de 64px quebraria em duas linhas e
          desalinharia a legenda — `is-text` dá o tamanho de texto, não de número. */}
      <div className={`lv2-stat-value ${numero ? "" : "is-text"}`}>
        {prefix}
        {exibido}
        {suffix}
      </div>
      <div className="lv2-stat-label">{label}</div>
    </div>
  );
}

// ============================================================================
// ANCORAGEM DE PREÇO — usada na waitlist (seção 001) e na página de vendas (seção 002).
// Fonte única de propósito: preço e valores de terceiro são fato de negócio, não de página —
// duas cópias divergiriam a primeira vez que o dono atualizasse um valor.
// ============================================================================
/**
 * ⚠️ VALORES DE TERCEIRO (dono, 2026-09-05): são preços de OUTRAS empresas numa página pública —
 * chutar número aqui é publicar afirmação falsa sobre produto de outra empresa. Linha com `null`
 * renderiza "—". Confirmar antes de mudar qualquer valor.
 */
export const CUSTOS_HOJE: { item: string; valor: number | null }[] = [
  { item: "Plataforma Profissional", valor: 450 },
  { item: "Market data Internacional", valor: 1039 },
  { item: "Serviço de notícia", valor: 2900 },
  { item: "Assinatura de research", valor: 300 },
  { item: "Módulos adicionais", valor: 1000 },
];

/**
 * Ordinal em português pro rótulo do lote ("Primeiro Lote", "Segundo Lote"...). Cobre até o 20º —
 * além disso (improvável: seria o vigésimo primeiro lote da mesma pré-venda) cai no fallback
 * "Lote Nº X", que nunca quebra mesmo se o contador crescer bem além do previsto.
 */
const ORDINAIS_PT = [
  "", "Primeiro", "Segundo", "Terceiro", "Quarto", "Quinto", "Sexto", "Sétimo", "Oitavo", "Nono", "Décimo",
  "Décimo Primeiro", "Décimo Segundo", "Décimo Terceiro", "Décimo Quarto", "Décimo Quinto",
  "Décimo Sexto", "Décimo Sétimo", "Décimo Oitavo", "Décimo Nono", "Vigésimo",
];

export function loteOrdinalLabel(loteNumber: number): string {
  const ordinal = ORDINAIS_PT[loteNumber];
  return ordinal ? `${ordinal} Lote` : `Lote Nº ${loteNumber}`;
}

/**
 * `precoAtual`/`loteLabel` são a única exceção ao "não hardcode preço/lote" desta tabela: a
 * waitlist não tem checkout, então não tem de onde buscar o valor real — usa os defaults (Primeiro
 * Lote, preço conhecido). A página de vendas (`home-page.tsx`) passa os valores de verdade,
 * vindos de `getFoundingRemaining()` (`plans.priceCents`/`founding_access_config.currentLote`, os
 * mesmos campos que a cobrança e o contador de lote usam) — nunca deixar essas props hardcoded lá.
 */
export function AnchorTable({ precoAtual = "R$139", loteLabel = "Primeiro Lote" }: { precoAtual?: string; loteLabel?: string }) {
  const brl = (n: number) => n.toLocaleString("pt-BR", { style: "currency", currency: "BRL", minimumFractionDigits: 0 });
  // Somado, nao escrito a mao: o total ja esteve dessincronizado dos itens (dizia "R$350" com a
  // lista somando outra coisa). Num quadro de comparacao de preco, total errado nao e deslize de
  // layout — e numero falso numa pagina publica.
  const totalHoje = CUSTOS_HOJE.reduce((acc, c) => acc + (c.valor ?? 0), 0);
  return (
    <div className="hts-anchor">
      <div className="hts-anchor-col">
        {/* "Sem HTS" / "Com HTS" (dono, 2026-09-07): "o que você paga hoje" vs "o que você vai
            pagar" obrigava a ler as duas colunas pra perceber que era uma comparação. */}
        <div className="hts-anchor-head">Sem HTS</div>
        <ul className="hts-anchor-list">
          {CUSTOS_HOJE.map((c) => (
            <li key={c.item}>
              <span>{c.item}</span>
              <span className="val">{c.valor == null ? "—" : brl(c.valor)}</span>
            </li>
          ))}
          <li>
            <span>A planilha que você monta na mão toda semana</span>
            <span className="val">—</span>
          </li>
        </ul>
        <div className="hts-anchor-total">Mais de {brl(totalHoje)}/mês</div>
      </div>

      <div className="hts-anchor-col is-hts">
        <div className="hts-anchor-head">Com HTS</div>
        <div className="hts-anchor-price">
          {precoAtual}<span>/mês</span>
        </div>
        <div className="hts-anchor-note">{loteLabel} · travado 12 meses</div>
        <div className="hts-anchor-price is-public">
          R$230<span>/mês</span>
        </div>
        <div className="hts-anchor-note">Preço público, depois</div>
        <div className="hts-anchor-total">Tudo incluso</div>
      </div>
    </div>
  );
}

// ============================================================================
// FOOTER — `navLinks` deixa cada página passar os próprios anchors internos
// ============================================================================
const DEFAULT_NAV_LINKS = [
  { href: "/", label: "Início" },
  { href: "/#ch-002", label: "A História" },
  { href: "/founding", label: "Founding Access" },
];

/** `rodape`: a linha da direita no pé da página (a home oficial troca o "Founding Access · Pré-venda"). */
/** `disclaimer` (18/10/2026, dono): texto legal no pé da página, recolhível, como o "Leia o disclaimer" de sites de assessoria. Aberto de início. */
/** `idDoDisclaimer` (20/10): âncora do bloco. Um link `#id` na página (o "condições da oferta" do checkout) abre o bloco se estiver fechado. */
export function Footer({ navLinks = DEFAULT_NAV_LINKS, rodape = "Founding Access · Pré-venda", disclaimer, tituloDoDisclaimer = "Leia o disclaimer", idDoDisclaimer }: { navLinks?: { href: string; label: string }[]; rodape?: string; disclaimer?: React.ReactNode; tituloDoDisclaimer?: string; idDoDisclaimer?: string }) {
  const year = new Date().getFullYear();
  const [disclaimerAberto, setDisclaimerAberto] = useState(true);
  useEffect(() => {
    if (!idDoDisclaimer) return;
    const alvo = `#${idDoDisclaimer}`;
    const abrir = (e: MouseEvent) => {
      const link = e.target instanceof Element ? e.target.closest("a") : null;
      if (link?.getAttribute("href") === alvo) setDisclaimerAberto(true);
    };
    document.addEventListener("click", abrir);
    return () => document.removeEventListener("click", abrir);
  }, [idDoDisclaimer]);
  return (
    <footer className="lv2-footer">
      <div className="lv2-footer-grid">
        <div className="lv2-footer-col">
          <span className="hts-wordmark" style={{ display: "block", marginBottom: 24, fontSize: 24 }}>HTS</span>
          <p style={{ color: "#888", fontSize: 14, lineHeight: 1.6, maxWidth: 380 }}>
            HTS — High Trading System. De um .exe simples, em 2021, à plataforma
            completa — construída por quem também opera.
          </p>
        </div>
        <div className="lv2-footer-col">
          <h4>HTS</h4>
          <ul>
            {navLinks.map((l) => (
              <li key={l.href}><a href={l.href}>{l.label}</a></li>
            ))}
          </ul>
        </div>
        <div className="lv2-footer-col">
          <h4>Contato</h4>
          <ul>
            <li><a href="mailto:hts@hightrading.com.br">hts@hightrading.com.br</a></li>
          </ul>
        </div>
      </div>
      {disclaimer ? (
        <div id={idDoDisclaimer} style={{ marginTop: 40, scrollMarginTop: 24 }}>
          <button type="button" onClick={() => setDisclaimerAberto((a) => !a)} aria-expanded={disclaimerAberto}
            style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10, width: "100%", background: "none", border: 0, borderBottom: "2px solid #ff00b8", padding: "0 0 10px", color: "#888", fontSize: 14, cursor: "pointer" }}>
            {tituloDoDisclaimer}
            <span aria-hidden style={{ display: "inline-block", transform: disclaimerAberto ? "rotate(180deg)" : "none", transition: "transform .2s" }}>⌄</span>
          </button>
          {disclaimerAberto ? <div style={{ padding: "24px 0 8px" }}>{disclaimer}</div> : null}
        </div>
      ) : null}
      <div className="lv2-footer-bottom">
        <span>© {year} HTS · São Paulo, BR</span>
        <span>{rodape}</span>
      </div>
    </footer>
  );
}
